import { Alert, Button, Group, Text } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { ReactNode, useState } from 'react';

type Props = {
  title: string;
  onRetry: () => void | Promise<unknown>;
  testId?: string;
} & (
  | {
      /** Whose data did not load, written as the sentence needs it: "Alice's", "the household's". */
      subject: string;
      /** What did not load. Name only the reads that actually failed, never the whole tab's worth. */
      what: string;
      children?: never;
    }
  | {
      /** Replaces the standard sentence where a case has to say something else — a partial failure. */
      children: ReactNode;
      subject?: never;
      what?: never;
    }
);

/**
 * What a tab shows in place of its figures when the data behind them did not load. A read that
 * failed is not a client who has nothing recorded, so the totals are withheld rather than struck
 * at zero: a zero here reads as "nothing to see" and invites the adviser to enter it all again.
 */
export function DataLoadError({ title, subject, what, children, onRetry, testId }: Props) {
  const [isRetrying, setIsRetrying] = useState(false);

  const retry = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } catch {
      // The read failed again, and the message above already says what to do about it.
    }
    setIsRetrying(false);
  };

  return (
    <Alert
      variant="light"
      color="red"
      icon={<IconAlertTriangle size={18} />}
      title={title}
      data-testid={testId}
    >
      <Text size="sm">
        {children ?? (
          <>
            We couldn&apos;t load {subject} {what}. Nothing has been lost; try again in a moment.
          </>
        )}
      </Text>
      <Group gap="xs" mt="sm">
        <Button
          size="xs"
          variant="light"
          color="red"
          leftSection={<IconRefresh size={14} />}
          loading={isRetrying}
          onClick={() => void retry()}
        >
          Try again
        </Button>
      </Group>
    </Alert>
  );
}
